/**
 * @fileoverview Archive/Restore prompt for a prospect, with its mandatory reason note.
 * @description Archiving or restoring a prospect always records why, so Confirm stays disabled
 * until a reason is typed. Split out of TenantDetailModal so the same prompt can also be opened on
 * its own, from a table row's actions menu (see TenantActionModal). Owns its own reason text and
 * mutation; the caller only decides what happens once it's done or cancelled.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useArchiveTenant } from '@/features/sales/hooks/useArchiveTenant'
import { useRestoreTenant } from '@/features/sales/hooks/useRestoreTenant'
import Label from '@/shared/components/atoms/Label'

import DialogActions from './DialogActions'

interface ReasonNotePanelProps {
  mode: 'archive' | 'restore'
  tenantId: string
  onDone: () => void
  onCancel: () => void
}

/**
 * @component
 * @param {ReasonNotePanelProps} props - Which change to make, to which tenant, and the handlers.
 * @param {'archive' | 'restore'} props.mode - Archive or restore the prospect.
 * @param {string} props.tenantId - The prospect to change.
 * @param {() => void} props.onDone - Called once the change is saved.
 * @param {() => void} props.onCancel - Called when the prompt is cancelled.
 */
function ReasonNotePanel({ mode, tenantId, onDone, onCancel }: ReasonNotePanelProps) {
  const { t } = useTranslation()
  const archiveTenant = useArchiveTenant()
  const restoreTenant = useRestoreTenant()
  const [reason, setReason] = useState('')
  const isBlank = reason.trim() === ''

  /**
   * @description Saves the archive or restore with the typed reason.
   */
  const submit = () => {
    if (isBlank) return
    const mutation = mode === 'archive' ? archiveTenant : restoreTenant
    mutation.mutate({ id: tenantId, reason }, { onSuccess: onDone })
  }

  return (
    <div
      className="flex flex-col gap-2.5 rounded-lg border border-row-border bg-surface-hover p-3.5"
      data-testid={`${mode}-prospect-panel`}
    >
      <span className="text-[12.5px] font-bold text-ink" data-testid={`${mode}-prospect-title`}>
        {t(`sales.tenantDetail.reasonNote.${mode === 'archive' ? 'archiveTitle' : 'restoreTitle'}`)}
      </span>
      <div>
        <Label htmlFor="reasonNote">{t('sales.tenantDetail.reasonNote.label')}</Label>
        <textarea
          id="reasonNote"
          data-testid={`${mode}-prospect-reason-input`}
          rows={2}
          placeholder={t('sales.tenantDetail.reasonNote.placeholder')}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          className="mt-1.5 w-full rounded-lg border border-input-border bg-white px-3 py-2 text-[13.5px] text-ink focus:outline-none focus:ring-2 focus:ring-primary/20"
        />
        {isBlank && (
          <span
            className="mt-1 block text-[11.5px] font-medium text-danger"
            data-testid={`${mode}-prospect-reason-error`}
          >
            {t('sales.tenantDetail.reasonNote.errors.reasonRequired')}
          </span>
        )}
      </div>
      <DialogActions
        testIdPrefix={`${mode}-prospect`}
        cancelLabel={t('sales.tenantDetail.reasonNote.cancel')}
        confirmLabel={t('sales.tenantDetail.reasonNote.submit')}
        onCancel={onCancel}
        onConfirm={submit}
        isLoading={archiveTenant.isPending || restoreTenant.isPending}
        disabled={isBlank}
      />
    </div>
  )
}

export default ReasonNotePanel
